import {ResourceType} from '../../types/resourceCenter'

export const RESOURCE_TYPE_OPTIONS = [
  {value: 'video', label: 'Video Tutorial'},
  {value: 'document', label: 'Document'},
  {value: 'template', label: 'Template'},
  {value: 'format', label: 'Format'},
  {value: 'guide', label: 'Guide'},
  {value: 'faq', label: 'FAQ'},
  {value: 'other', label: 'Other'},
]

// El backend (graphene) puede devolver el tipo en MAYÚSCULAS ('VIDEO') por la
// conversión automática de choices a Enum — normalizamos siempre a minúsculas
export const normalizeResourceType = (type: string): ResourceType =>
  ((type || '').toLowerCase() as ResourceType)

export const getResourceTypeLabel = (type: string): string => {
  const normalized = normalizeResourceType(type)
  return RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === normalized)?.label || type
}

export const getResourceTypeIcon = (type: ResourceType): string => {
  const icons: Record<ResourceType, string> = {
    video: '📹',
    document: '📄',
    template: '📋',
    format: '📑',
    guide: '📖',
    faq: '❓',
    other: '📎',
  }
  return icons[normalizeResourceType(type)] || '📎'
}

export const getResourceTypeColor = (type: ResourceType): string => {
  const colors: Record<ResourceType, string> = {
    video: '#e74c3c', // Red
    document: '#3498db', // Blue
    template: '#9b59b6', // Purple
    format: '#1abc9c', // Teal
    guide: '#f39c12', // Orange
    faq: '#2ecc71', // Green
    other: '#95a5a6', // Gray
  }
  return colors[normalizeResourceType(type)] || '#95a5a6'
}

export const getResourceTypeBadgeStyle = (type: ResourceType) => ({
  backgroundColor: getResourceTypeColor(type),
  color: 'white',
  padding: '4px 12px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
})

export const getFileExtension = (fileName: string): string => {
  return fileName.split('?')[0].split('.').pop()?.toLowerCase() || ''
}

export const getFileIcon = (fileName: string): JSX.Element => {
  const ext = getFileExtension(fileName)

  const iconMap: Record<string, string> = {
    pdf: 'bi-file-pdf',
    doc: 'bi-file-word',
    docx: 'bi-file-word',
    xls: 'bi-file-excel',
    xlsx: 'bi-file-excel',
    ppt: 'bi-file-ppt',
    pptx: 'bi-file-ppt',
    zip: 'bi-file-zip',
    rar: 'bi-file-zip',
    mp4: 'bi-file-play',
    avi: 'bi-file-play',
    mov: 'bi-file-play',
    mp3: 'bi-file-music',
    wav: 'bi-file-music',
    jpg: 'bi-file-image',
    jpeg: 'bi-file-image',
    png: 'bi-file-image',
    gif: 'bi-file-image',
    svg: 'bi-file-image',
    txt: 'bi-file-text',
    csv: 'bi-file-spreadsheet',
  }

  const iconClass = iconMap[ext] || 'bi-file-earmark'

  return <i className={`bi ${iconClass} fs-2`} />
}

export const isImageFile = (fileName: string): boolean => {
  const ext = getFileExtension(fileName)
  return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)
}

export const isVideoFile = (fileName: string): boolean => {
  const ext = getFileExtension(fileName)
  return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(ext)
}

export const isPdfFile = (fileName: string): boolean => {
  const ext = getFileExtension(fileName)
  return ext === 'pdf'
}
